import { selectCurrentHotelName } from 'accessors'
import {
  PrimaryRegularButton,
  SecondaryRegularButton
} from 'components/ReusableComponents/Button/RegularButtons'
import Dialog from 'components/ReusableComponents/Dialog'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import TextInputWithEmoji from 'components/ReusableComponents/ManageHotel/TextInputWithEmoji'
import { TextPreview, TextPreviewBubble } from 'features/ManageCampaigns/TextPreview'
import { parentChains } from 'main/hotelBrandsAndChains'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import styles from './EditPromoDialog.module.scss'

const nightsToStayOptions = {
  1: '1 night',
  2: '2 nights',
  3: '3 nights'
}

export const EditWidgetPromoWithoutDialog = ({
  contentId,
  promotionCode: defaultPromoCode,
  minNightsToStay: defaultMinNights,
  maxDiscountPercentage: defaultMaxDiscount,
  promotionTagline: defaultPromoTagline,
  messageTemplate,
  onSubmit,
  isSaving
}) => {
  const [promotionCode, setPromotionCode] = useState(defaultPromoCode)
  const [minNightsToStay, setMinNightsToStay] = useState(defaultMinNights)
  const [maxDiscountPercentage, setMaxDiscountPercentage] = useState(defaultMaxDiscount)
  const [promotionTagline, setPromotionTagline] = useState(defaultPromoTagline)

  const hotelName = useSelector(selectCurrentHotelName)

  function handleSubmit() {
    if (promotionCode && promotionTagline) {
      onSubmit({
        contentId,
        promotionCode,
        minNightsToStay,
        maxDiscountPercentage,
        promotionTagline
      })
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        maxHeight: 400,
        overflow: 'hidden',
        borderBottom: '1px solid #dedede'
      }}
    >
      <div>
        <div className={styles.titleHolder}>
          <p className={styles.title}>Set up opt-in promotion</p>
        </div>
        <EditWidgetPromo
          promotionCode={promotionCode}
          setPromotionCode={setPromotionCode}
          minNightsToStay={minNightsToStay}
          setMinNightsToStay={setMinNightsToStay}
          maxDiscountPercentage={maxDiscountPercentage}
          setMaxDiscountPercentage={setMaxDiscountPercentage}
          promotionTagline={promotionTagline}
          setPromotionTagline={setPromotionTagline}
        />
        <PrimaryRegularButton
          className={styles.saveChangesButton}
          onClick={handleSubmit}
          disabled={isSaving}
        >
          {isSaving ? 'Saving promotion...' : 'Save promotion'}
        </PrimaryRegularButton>
      </div>
      <TextPreview>
        <TextPreviewBubble
          text={messageTemplate}
          defaultData={{
            userName: 'John Doe',
            hotelName,
            promotionName: promotionTagline
          }}
        />
      </TextPreview>
    </div>
  )
}

export const EditWidgetPromo = ({
  promotionCode,
  setPromotionCode,
  minNightsToStay,
  setMinNightsToStay,
  maxDiscountPercentage,
  setMaxDiscountPercentage,
  promotionTagline,
  setPromotionTagline
}) => {
  const parentChainCode = useSelector(s => s.hotelDetails.parentChainCode)

  function getWordRateCode() {
    if (parentChainCode === parentChains.ihg || parentChainCode === parentChains.choice) {
      return 'rate code'
    }

    if (parentChainCode === parentChains.wyndham) {
      return 'corporate ID '
    }

    return 'promo code'
  }

  const wordRateCode = getWordRateCode()

  function handlePercentChange(e) {
    const value = e.target.value

    if (value > 70) {
      setMaxDiscountPercentage(70)
    } else if (value < 0) {
      setMaxDiscountPercentage(0)
    } else {
      setMaxDiscountPercentage(value)
    }
  }

  const isPromoValid = !!promotionCode?.match(/^[A-Za-z0-9]{1,15}$/gm)

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.discountHolder}>
          <div className={styles.inputRow}>
            <TextInputWithEmoji
              label="Promo name"
              className={styles.textInput}
              value={promotionTagline}
              valid={!!promotionTagline}
              onValueChange={setPromotionTagline}
              maxLength={20}
            />
          </div>
          <div className={styles.halfInputRowContainer}>
            <div className={styles.halfInputRow}>
              <TextInput
                label={`Offer ${wordRateCode}`}
                className={styles.textInput}
                value={promotionCode || ''}
                valid={isPromoValid}
                onChange={e => setPromotionCode(e.target.value)}
              />
              {promotionCode && !isPromoValid && (
                <p className={styles.validationError}>Enter a valid promo code</p>
              )}
            </div>
            <div className={styles.halfInputRow}>
              <Dropdown
                options={nightsToStayOptions}
                label="Minumum nights to stay"
                value={minNightsToStay}
                onChange={e => setMinNightsToStay(e.target.value)}
              />
            </div>
          </div>
          <div className={styles.halfInputRowContainer}>
            <div className={styles.halfInputRow}>
              <TextInput
                label="Offer discount percentage"
                value={maxDiscountPercentage ?? 0}
                type="number"
                className={styles.textInput}
                min={0}
                max={70}
                onChange={handlePercentChange}
                onBlur={e =>
                  e.target.value === '' ? setMaxDiscountPercentage(0) : false
                }
              />
            </div>
            <div className={styles.halfInputRow}></div>
          </div>
        </div>
      </div>
    </>
  )
}

const EditWidgetPromoDialog = ({
  contentId,
  promotionCode: defaultPromoCode,
  minNightsToStay: defaultMinNights,
  maxDiscountPercentage: defaultMaxDiscount,
  promotionTagline: defaultPromoTagline,
  messageTemplate,
  onSave,
  isSaving,
  onClose
}) => {
  const [promotionCode, setPromotionCode] = useState(defaultPromoCode)
  const [minNightsToStay, setMinNightsToStay] = useState(defaultMinNights)
  const [maxDiscountPercentage, setMaxDiscountPercentage] = useState(defaultMaxDiscount)
  const [promotionTagline, setPromotionTagline] = useState(defaultPromoTagline)

  const hotelName = useSelector(selectCurrentHotelName)

  return (
    <>
      <Dialog>
        <div className={styles.dialogWrapper}>
          <div className={styles.dialog}>
            <div className={styles.dialogHeader}>
              <div className={styles.dialogTitle}>Opt-in promotion</div>
            </div>
            <div className={styles.dialogContent}>
              <EditWidgetPromo
                promotionCode={promotionCode}
                setPromotionCode={setPromotionCode}
                minNightsToStay={minNightsToStay}
                setMinNightsToStay={setMinNightsToStay}
                maxDiscountPercentage={maxDiscountPercentage}
                setMaxDiscountPercentage={setMaxDiscountPercentage}
                promotionTagline={promotionTagline}
                setPromotionTagline={setPromotionTagline}
              />
            </div>
            <div className={styles.dialogFooter}>
              <SecondaryRegularButton onClick={onClose}>Discard</SecondaryRegularButton>
              <PrimaryRegularButton
                disabled={!promotionCode || !promotionTagline || isSaving}
                onClick={() =>
                  onSave({
                    contentId,
                    promotionCode,
                    minNightsToStay,
                    maxDiscountPercentage,
                    promotionTagline
                  })
                }
              >
                {isSaving ? 'Saving promotion...' : 'Save promotion'}
              </PrimaryRegularButton>
            </div>
          </div>
        </div>
        <TextPreview>
          <TextPreviewBubble
            text={messageTemplate}
            defaultData={{
              userName: 'John Doe',
              hotelName,
              promotionName: promotionTagline
            }}
          />
        </TextPreview>
      </Dialog>
    </>
  )
}

export default EditWidgetPromoDialog
